import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { CheckCircle, ArrowRight, Star, Zap, Users, Globe, Shield, FileText, Link2, BarChart3, Lightbulb, Search, Code, Image, Languages, BadgeCheck, Info, ChevronDown, ChevronUp } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { LANGUAGES_SIMPLE } from "@/lib/languages";
import { useSignIn } from "@/hooks/useSignIn";

// Supported languages - matching what's shown in My Content tab article settings
const POPULAR_LANGUAGE_CODES = [
  // Major European Languages
  'en', 'es', 'fr', 'de', 'it', 'pt', 'nl', 'pl', 'sv', 'no', 'da', 'fi', 'cs', 'hu', 'ro', 'bg', 'hr', 'sk', 'sl', 'tr', 'uk', 'el', 'lt', 'lv', 'et', 'mk', 'sq', 'sr', 'bs', 'me', 'ca', 'eu', 'gl', 'cy', 'ga', 'gd', 'mt', 'is', 'fo', 'lb',
  // Asian Languages
  'ja', 'ko', 'zh', 'zh-tw', 'hi', 'bn', 'ur', 'ta', 'te', 'ml', 'kn', 'gu', 'pa', 'th', 'vi', 'id', 'ms', 'tl', 'my', 'km', 'lo',
  // Middle Eastern & African Languages
  'ar', 'fa', 'he', 'am', 'sw', 'ha', 'yo', 'ig', 'zu', 'af', 'xh',
  // Americas Regional Variants
  'pt-br', 'es-mx', 'es-ar', 'fr-ca'
];

// Get only the supported languages (excluding 'ru' and 'be')
const SUPPORTED_LANGUAGES = LANGUAGES_SIMPLE
  .filter(l => POPULAR_LANGUAGE_CODES.includes(l.value))
  .filter(l => l.value !== 'ru' && l.value !== 'be');

interface PricingProps {
  onSignIn: () => void;
}

const Pricing: React.FC<PricingProps> = ({ onSignIn }) => {
  const { signingIn } = useSignIn();
  
  const handleClick = async () => {
    if (signingIn) return;
    await onSignIn();
  };
  const [isExpanded, setIsExpanded] = useState(false);
  const features = [
    "30 SEO-optimized articles generated and published monthly",
    "10 premium backlinks delivered monthly (DA 70+)",
    "1 comprehensive website analysis per month",
    "Topic clusters and 30 fresh article ideas included",
    "Google Search Console integration and tracking",
    "LLM.txt file generation for AI search optimization",
    "Publish directly to WordPress, Webflow, and Shopify (Framer coming soon)",
    "AI-generated images in multiple styles",
    `Content generation in ${SUPPORTED_LANGUAGES.length} languages`,
    "30-day money-back guarantee"
  ];

  const featureIcons = [
    <FileText key="articles" className="h-4 w-4" />,
    <Link2 key="backlinks" className="h-4 w-4" />,
    <BarChart3 key="analysis" className="h-4 w-4" />,
    <Lightbulb key="ideas" className="h-4 w-4" />,
    <Search key="gsc" className="h-4 w-4" />,
    <Code key="llm" className="h-4 w-4" />,
    <Globe key="integrations" className="h-4 w-4" />,
    <Image key="images" className="h-4 w-4" />,
    <Languages key="languages" className="h-4 w-4" />,
    <BadgeCheck key="guarantee" className="h-4 w-4" />
  ];

  return (
    <section id="pricing" className="w-full py-16 sm:py-20 lg:py-24 bg-muted/20 scroll-mt-32 sm:scroll-mt-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16 sm:mb-20">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground mb-4">
              Start <span className="text-primary">growing your traffic</span> today
            </h2>
            <p className="text-lg sm:text-xl text-muted-foreground max-w-3xl mx-auto">
              Get everything you need to rank higher and drive more organic traffic—all automated so you can focus on what matters most.
            </p>
          </div>

          {/* Social Proof */}
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-4 mb-4">
              <div className="flex -space-x-2">
                <img
                  src="https://ancujowsxivyfnxhnsmr.supabase.co/storage/v1/object/public/images/m2K2Wont_400x400.jpg"
                  alt="Customer avatar"
                  className="w-8 h-8 rounded-full border-2 border-white/70 shadow-sm object-cover"
                />
                <img
                  src="https://ancujowsxivyfnxhnsmr.supabase.co/storage/v1/object/public/images/BMTvrGgC_400x400.jpg"
                  alt="Customer avatar"
                  className="w-8 h-8 rounded-full border-2 border-white/70 shadow-sm object-cover"
                />
                <img
                  src="https://ancujowsxivyfnxhnsmr.supabase.co/storage/v1/object/public/images/avatar-4.jpg"
                  alt="Customer avatar"
                  className="w-8 h-8 rounded-full border-2 border-white/70 shadow-sm object-cover"
                />
                <img
                  src="https://ancujowsxivyfnxhnsmr.supabase.co/storage/v1/object/public/images/avatar-3.jpg"
                  alt="Customer avatar"
                  className="w-8 h-8 rounded-full border-2 border-white/70 shadow-sm object-cover"
                />
              </div>
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-yellow-400/80 text-yellow-400/80" />
                ))}
              </div>
            </div>
            <p className="text-muted-foreground font-medium">5k+ Articles Created</p>
          </div>

          {/* Single Horizontal Pricing Component */}
          <div className="bg-background rounded-3xl shadow-2xl border border-border/50 overflow-hidden max-w-6xl mx-auto">
            <div className="flex flex-col lg:flex-row">
              {/* Left Side - Pricing Info */}
              <div className="lg:w-2/5 p-8 sm:p-10 bg-primary/5 flex items-center">
                <div className="text-center lg:text-left w-full">
                  {/* Title and Badge - Better alignment */}
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
                    <h3 className="text-2xl sm:text-3xl font-bold text-foreground">
                      All in One
                    </h3>
                    <div className="inline-flex items-center gap-2 bg-gradient-to-r from-primary via-primary/95 to-primary text-white px-4 py-2 rounded-full text-xs sm:text-sm font-bold border-2 border-primary/30 shadow-lg shadow-primary/30 hover:shadow-primary/50 transition-all duration-300 hover:scale-105 w-fit mx-auto sm:mx-0">
                      <span>Special Offer - <span className="text-yellow-300">40% OFF</span></span>
                    </div>
                  </div>

                  {/* Limited Spots Urgency & Price Lock - Expandable */}
                  <div className="mb-4 rounded-lg bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/30 dark:to-emerald-950/30 border-2 border-green-300 dark:border-green-700 overflow-hidden shadow-md">
                    <button
                      onClick={() => setIsExpanded(!isExpanded)}
                      className="w-full p-3 flex items-center justify-between gap-2 text-center sm:text-left hover:bg-green-100/50 dark:hover:bg-green-950/50 transition-colors"
                    >
                      <p className="text-xs sm:text-sm font-semibold text-green-700 dark:text-green-300 flex-1">
                        ⚡ Limited spots available - Lock in this price forever{!isExpanded && '...'}
                      </p>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info 
                                className="h-4 w-4 text-green-600 dark:text-green-400 cursor-help" 
                                onClick={(e) => e.stopPropagation()}
                              />
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs" side="top">
                              <p className="text-sm">
                                <strong>Price Lock Guarantee:</strong> When you subscribe at $87.90/month, this price is locked in for as long as you remain subscribed. Even if we add new features, increase prices for new customers, or change our pricing structure, your monthly rate will always stay at $87.90 until you choose to cancel. This is your lifetime rate.
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        {isExpanded ? (
                          <ChevronUp className="h-4 w-4 text-green-600 dark:text-green-400" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-green-600 dark:text-green-400" />
                        )}
                      </div>
                    </button>
                    {isExpanded && (
                      <div className="px-3 pb-3 pt-0">
                        <p className="text-xs sm:text-sm text-green-700 dark:text-green-300 font-medium">
                          Your price stays at $87.90/month forever, even as we add new features. This is your lifetime rate—guaranteed.
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Pricing - Better alignment */}
                  <div className="flex flex-col items-center lg:items-start gap-2 mb-4">
                    <div className="flex items-start gap-3 justify-center lg:justify-start">
                      <span className="text-5xl sm:text-6xl font-bold text-primary">$87.90</span>
                      <div className="flex flex-col items-center mt-0.5">
                        <span className="text-xs sm:text-sm text-green-600 dark:text-green-400 font-semibold mb-0.5">Save $61.10/month</span>
                        <span className="text-xl sm:text-2xl font-medium text-muted-foreground line-through whitespace-nowrap">$149 /monthly</span>
                      </div>
                    </div>
                  </div>


                  {/* CTA Button */}
                  <Button
                    onClick={handleClick}
                    disabled={signingIn}
                    size="lg"
                    className="rounded-full text-base sm:text-lg px-8 py-6 font-bold bg-gradient-to-r from-primary via-primary to-primary/90 text-primary-foreground hover:from-primary/95 hover:via-primary/90 hover:to-primary/85 transition-all duration-300 flex items-center justify-center gap-3 shadow-xl shadow-primary/30 hover:shadow-primary/50 min-h-[64px] w-full mb-3 relative overflow-hidden group hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                  >
                    <span className="relative z-10 font-bold tracking-wide">Lock In This Price Now</span>
                    <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform duration-200 relative z-10" />
                    <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 transform translate-x-[-100%] group-hover:translate-x-0 transition-transform duration-500"></div>
                  </Button>
                  
                  {/* Trust Signals */}
                  <div className="flex flex-col items-center gap-2 mb-2">
                    <div className="flex items-center gap-2 text-xs sm:text-sm text-muted-foreground">
                      <Shield className="h-4 w-4 text-green-600 dark:text-green-400" />
                      <span className="font-semibold">30-Day Money-Back Guarantee</span>
                    </div>
                    <p className="text-xs sm:text-sm text-muted-foreground text-center">
                      <span className="font-bold">Cancel anytime.</span> No questions asked!
                    </p>
                  </div>
                </div>
              </div>

              {/* Vertical Separator Line */}
              <div className="hidden lg:flex lg:flex-col lg:items-center lg:justify-center lg:px-4">
                <div className="w-px h-full bg-black/10 min-h-[210px] my-16 rounded-full"></div>
              </div>

              {/* Right Side - Features */}
              <div className="lg:w-3/5 p-8 sm:p-10 bg-background">
                <div className="mb-8">
                  <h4 className="text-2xl font-bold text-foreground mb-2">What's included:</h4>
                </div>

                {/* Features Grid - 2 columns on larger screens */}
                <div className="grid sm:grid-cols-2 gap-4">
                  {features.map((feature, index) => (
                    <div key={index} className="flex items-start gap-3 p-3 rounded-xl bg-muted/30 hover:bg-muted/50 transition-colors duration-200 group">
                      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-primary group-hover:bg-primary/20 transition-colors duration-200">
                        {featureIcons[index]}
                      </div>
                      <span className="text-foreground leading-relaxed text-sm font-medium">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Bottom CTA */}
          <div className="text-center mt-12">
            <div className="bg-primary/10 rounded-2xl p-8 border border-primary/20 max-w-4xl mx-auto">
              <h4 className="text-xl font-bold text-foreground mb-3">Ready to dominate your niche?</h4>
              <p className="text-muted-foreground mb-6">Join thousands of entrepreneurs who are already ranking higher and growing their organic traffic—while they focus on what matters most.</p>
              <Button
                onClick={handleClick}
                disabled={signingIn}
                size="lg"
                className="rounded-full text-lg px-8 py-4 font-semibold bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-300 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Get Started Now
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
